/**
 * Product page behaviour: gallery, variant selection, quantity and the sticky
 * buy bar.
 *
 * The add-to-cart form itself is the theme's own `<product-form-component>`, so
 * this file never posts to the cart. It only keeps the presentation in sync with
 * whichever variant is selected, and writes the resolved variant id into the two
 * hidden inputs (the AJAX form and the no-JS "Buy it now" form).
 *
 * Everything degrades: with JavaScript off the radios still submit the chosen
 * option values, the first variant is pre-selected, and the gallery shows the
 * featured image.
 */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-pdp]';

  /* ------------------------------------------------------------------ money */

  /**
   * Shopify's money_without_trailing_zeros filter, reimplemented against the shop's
   * own money_format so the prices this script writes are byte-identical to the ones
   * Liquid rendered. Using Intl here instead would show "$749.00" on a store whose
   * format is "${{amount_no_decimals}}", which flashes the moment the script runs.
   *
   * "Without trailing zeros" means a whole amount drops the decimals ($899) while a
   * fractional one keeps them ($1,249.50). The explicit *_no_decimals placeholders
   * are left alone: those are the merchant's own choice to always round.
   */
  function formatMoney(root, cents) {
    var format = root.getAttribute('data-money-format') || '${{amount}}';
    var isWhole = cents % 100 === 0;

    function thousands(precision, thousandSep, decimalSep) {
      var value = (cents / 100).toFixed(precision);
      var parts = value.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousandSep);
      var fraction = parts[1] ? decimalSep + parts[1] : '';
      return whole + fraction;
    }

    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (match, name) {
      switch (name) {
        case 'amount':
          return thousands(isWhole ? 0 : 2, ',', '.');
        case 'amount_no_decimals':
          return thousands(0, ',', '.');
        case 'amount_with_comma_separator':
          return thousands(isWhole ? 0 : 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return thousands(0, '.', ',');
        case 'amount_with_space_separator':
          return thousands(isWhole ? 0 : 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator':
          return thousands(0, ' ', ',');
        case 'amount_with_apostrophe_separator':
          return thousands(isWhole ? 0 : 2, "'", '.');
        default:
          return match;
      }
    });
  }

  /* --------------------------------------------------------------- variants */

  function getVariants(root) {
    var script = root.querySelector('[data-pdp-variants]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent) || [];
    } catch (error) {
      return [];
    }
  }

  function getSelectedOptions(root) {
    var selected = [];
    root.querySelectorAll('[data-pdp-option]').forEach(function (group) {
      var index = parseInt(group.getAttribute('data-pdp-option'), 10);
      var checked = group.querySelector('input:checked');
      selected[index] = checked ? checked.value : null;
    });
    return selected;
  }

  function findVariant(variants, selected) {
    for (var i = 0; i < variants.length; i++) {
      var variant = variants[i];
      var match = true;
      for (var o = 0; o < selected.length; o++) {
        if (selected[o] == null) continue;
        if (variant.options[o] !== selected[o]) {
          match = false;
          break;
        }
      }
      if (match) return variant;
    }
    return null;
  }

  /* ---------------------------------------------------------------- gallery */

  function setMainMedia(root, src, alt, mediaId) {
    var main = root.querySelector('[data-pdp-main-image]');
    if (!main || !src) return;
    main.setAttribute('src', src);
    main.removeAttribute('srcset');
    if (typeof alt === 'string') main.setAttribute('alt', alt);
    if (mediaId != null) main.setAttribute('data-media-id', String(mediaId));
  }

  function selectThumb(root, thumb) {
    root.querySelectorAll('[data-pdp-thumb]').forEach(function (button) {
      button.setAttribute('aria-current', button === thumb ? 'true' : 'false');
    });
  }

  function showMediaForVariant(root, variant) {
    if (!variant || variant.media_id == null) return;
    var thumb = root.querySelector('[data-pdp-thumb][data-media-id="' + variant.media_id + '"]');
    if (!thumb) return;
    selectThumb(root, thumb);
    setMainMedia(root, thumb.getAttribute('data-full-src'), thumb.getAttribute('data-alt'), variant.media_id);
  }

  function bindGallery(root) {
    root.querySelectorAll('[data-pdp-thumb]').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        selectThumb(root, thumb);
        setMainMedia(root, thumb.getAttribute('data-full-src'), thumb.getAttribute('data-alt'), thumb.getAttribute('data-media-id'));
      });
    });
    bindViewer(root);
    bindPreorder(root);
  }

  /* ------------------------------------------------------ pre-order app */

  /* The pre-order app swaps the main add-to-cart for its own button, which
     adds the item with a "Pre-Order" line item property. The sticky bar's
     button submits the form directly and would skip that - adding a
     pre-order product as a normal order. When the app's button exists, the
     sticky button hands the click to it and takes its label. */
  function bindPreorder(root) {
    var sticky = root.querySelector('.pdp-sticky [data-pdp-submit]');
    if (!sticky) return;

    function preorderButton() {
      return root.querySelector('.pdp-add-to-cart .preOrderButton, form:not(.pdp-buy-now-form) .preOrderButton');
    }

    sticky.addEventListener('click', function (event) {
      var target = preorderButton();
      if (!target) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      target.click();
    }, true);

    /* Writes only when the text actually changes: assigning textContent,
       even to the same string, is itself a mutation, and an unconditional
       write from inside an observer re-triggers it forever. The observer
       also only watches the add-to-cart area, where the app injects, so the
       sticky label (outside it) can never feed back into it. */
    function setLabel(label, text) {
      if (text && label.textContent !== text) label.textContent = text;
    }

    /* The app paints its button with inline `!important` colours from its own
       admin settings, which no stylesheet can outrank. Dropping them lets the
       button take the theme's lime CTA style (it already carries its classes). */
    function restyle(target) {
      ['background-color', 'color', 'border'].forEach(function (prop) {
        if (target.style.getPropertyValue(prop)) target.style.removeProperty(prop);
      });
      var message = root.querySelector('.pdp-qty-row .preOrderMessage');
      if (message && message.style.getPropertyValue('margin-top')) message.style.removeProperty('margin-top');
    }

    /* On products that are not on pre-order the app still writes
       `display: block !important` on our add to cart button, which breaks its
       flex row (label, price, icon) onto two lines. Put flex back; the
       app's `display: none` on pre-order products is left alone. */
    function restoreSubmit() {
      var submit = root.querySelector('.pdp-add-to-cart [data-pdp-submit]');
      if (submit && submit.style.getPropertyValue('display') === 'block') {
        submit.style.setProperty('display', 'flex', 'important');
      }
    }

    function syncLabel() {
      var target = preorderButton();
      if (target) restyle(target);
      restoreSubmit();
      var label = sticky.querySelector('[data-pdp-submit-label]');
      if (!label) return;
      if (target) {
        if (!label.hasAttribute('data-default-label')) label.setAttribute('data-default-label', label.textContent);
        setLabel(label, (target.value || target.textContent || '').trim());
      } else if (label.hasAttribute('data-default-label')) {
        setLabel(label, label.getAttribute('data-default-label'));
      }
    }

    var watched = root.querySelector('.pdp-add-to-cart') || root.querySelector('product-form-component');
    /* Style attributes are watched too, since the app writes its inline styles
       after injecting. Every write above is guarded, so the observer settles. */
    if (watched) {
      new MutationObserver(syncLabel).observe(watched, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style']
      });
    }
    syncLabel();
  }

  /* ----------------------------------------------------------------- viewer */

  function bindViewer(root) {
    var dialog = root.querySelector('[data-pdp-viewer]');
    var opener = root.querySelector('[data-pdp-open-viewer]');
    var dataNode = root.querySelector('[data-pdp-viewer-media]');
    if (!dialog || !opener || !dataNode || typeof dialog.showModal !== 'function') return;

    var media;
    try {
      media = JSON.parse(dataNode.textContent);
    } catch (error) {
      return;
    }
    if (!Array.isArray(media) || media.length === 0) return;

    var image = dialog.querySelector('[data-pdp-viewer-image]');
    var count = dialog.querySelector('[data-pdp-viewer-count]');
    var countTemplate = root.getAttribute('data-image-counter') || '{{ index }} of {{ total }}';
    var index = 0;

    function show(next) {
      index = (next + media.length) % media.length;
      var item = media[index];
      if (item.width && item.height) {
        image.setAttribute('width', String(item.width));
        image.setAttribute('height', String(item.height));
      }
      image.setAttribute('src', item.src);
      image.setAttribute('alt', item.alt || '');
      if (count) {
        count.textContent = countTemplate
          .replace('{{ index }}', String(index + 1))
          .replace('{{ total }}', String(media.length));
      }
    }

    function indexOfCurrentMain() {
      var main = root.querySelector('[data-pdp-main-image]');
      var id = main ? main.getAttribute('data-media-id') : null;
      for (var i = 0; i < media.length; i += 1) {
        if (String(media[i].id) === String(id)) return i;
      }
      return 0;
    }

    function open() {
      show(indexOfCurrentMain());
      dialog.showModal();
    }

    opener.addEventListener('click', open);

    var closeButton = dialog.querySelector('[data-pdp-viewer-close]');
    if (closeButton) closeButton.addEventListener('click', function () { dialog.close(); });

    var prev = dialog.querySelector('[data-pdp-viewer-prev]');
    var next = dialog.querySelector('[data-pdp-viewer-next]');
    if (prev) prev.addEventListener('click', function () { show(index - 1); });
    if (next) next.addEventListener('click', function () { show(index + 1); });

    dialog.addEventListener('keydown', function (event) {
      if (media.length < 2) return;
      if (event.key === 'ArrowLeft') { event.preventDefault(); show(index - 1); }
      if (event.key === 'ArrowRight') { event.preventDefault(); show(index + 1); }
    });

    /* Clicks on the dialog element itself (not its children) are on the
       backdrop area around the image. */
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog || event.target.hasAttribute('data-pdp-viewer-stage')) dialog.close();
    });

    /* Drop the large image once closed so it does not linger in memory. */
    dialog.addEventListener('close', function () {
      image.removeAttribute('src');
    });
  }

  /* --------------------------------------------------------------- quantity */

  function getQuantity(root) {
    var input = root.querySelector('[data-pdp-qty]');
    if (!input) return 1;
    var value = parseInt(input.value, 10);
    if (isNaN(value) || value < 1) value = 1;
    return value;
  }

  function setQuantity(root, value) {
    var input = root.querySelector('[data-pdp-qty]');
    if (!input) return;
    input.value = String(Math.max(1, value));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function bindQuantity(root) {
    var input = root.querySelector('[data-pdp-qty]');
    var minus = root.querySelector('[data-pdp-qty-minus]');
    var plus = root.querySelector('[data-pdp-qty-plus]');

    if (minus) {
      minus.addEventListener('click', function () {
        setQuantity(root, getQuantity(root) - 1);
      });
    }
    if (plus) {
      plus.addEventListener('click', function () {
        setQuantity(root, getQuantity(root) + 1);
      });
    }
    if (input) {
      input.addEventListener('input', function () {
        update(root);
      });
      input.addEventListener('change', function () {
        input.value = String(getQuantity(root));
        update(root);
      });
    }
  }

  /* ------------------------------------------------------------------ paint */

  function setText(root, selector, value) {
    var node = root.querySelector(selector);
    if (node) node.textContent = value;
  }

  function setHidden(node, hidden) {
    if (node) node.hidden = hidden;
  }

  function update(root) {
    var variants = getVariants(root);
    if (!variants.length) return;

    var selected = getSelectedOptions(root);
    var variant = findVariant(variants, selected) || variants[0];
    var quantity = getQuantity(root);

    /* Hidden inputs: the AJAX form and the no-JS buy now form. */
    root.querySelectorAll('[data-pdp-variant-id]').forEach(function (input) {
      input.value = variant.id;
      input.disabled = !variant.available;
    });
    root.querySelectorAll('[data-pdp-qty-mirror]').forEach(function (input) {
      input.value = quantity;
    });

    /* Price block. */
    setText(root, '[data-pdp-price]', formatMoney(root, variant.price));

    var onSale = variant.compare_at_price != null && variant.compare_at_price > variant.price;
    var was = root.querySelector('[data-pdp-compare]');
    var off = root.querySelector('[data-pdp-savings]');
    setHidden(was, !onSale);
    setHidden(off, !onSale);
    if (onSale) {
      if (was) was.textContent = formatMoney(root, variant.compare_at_price);
      if (off) {
        var template = off.getAttribute('data-template') || '{{ amount }}';
        off.textContent = template.replace(
          '{{ amount }}',
          formatMoney(root, variant.compare_at_price - variant.price)
        );
      }
    }

    /* Instalments line. */
    var instalments = root.querySelector('[data-pdp-instalment]');
    if (instalments) {
      var parts = parseInt(instalments.getAttribute('data-parts'), 10) || 4;
      instalments.textContent = formatMoney(root, Math.round(variant.price / parts));
    }

    /* Selected option summary next to the option label. */
    var summary = root.querySelector('[data-pdp-option-summary]');
    if (summary) {
      summary.textContent = variant.title + ' - ' + formatMoney(root, variant.price);
    }

    /* Add to cart button price reflects the line total, as in the reference. */
    setText(root, '[data-pdp-line-total]', formatMoney(root, variant.price * quantity));

    /* Sticky bar. */
    setText(root, '[data-pdp-sticky-price]', formatMoney(root, variant.price));
    var stickyCompare = root.querySelector('[data-pdp-sticky-compare]');
    setHidden(stickyCompare, !onSale);
    if (onSale && stickyCompare) {
      stickyCompare.textContent = formatMoney(root, variant.compare_at_price);
    }

    /* Availability. */
    var soldOut = !variant.available;
    root.querySelectorAll('[data-pdp-submit]').forEach(function (button) {
      button.disabled = soldOut;
      var label = button.querySelector('[data-pdp-submit-label]');
      if (!label) return;
      var soldOutText = button.getAttribute('data-sold-out-text');
      var defaultText = button.getAttribute('data-default-text');
      if (soldOut && soldOutText) label.textContent = soldOutText;
      if (!soldOut && defaultText) label.textContent = defaultText;
    });

    showMediaForVariant(root, variant);

    /* Keep the address bar on the selected variant so the page is shareable. */
    if (variant.url && window.history && window.history.replaceState) {
      window.history.replaceState({}, '', variant.url);
    }

    root.dispatchEvent(
      new CustomEvent('pdp:variantchange', { bubbles: true, detail: { variant: variant, quantity: quantity } })
    );
  }

  function bindOptions(root) {
    root.querySelectorAll('[data-pdp-option] input').forEach(function (input) {
      input.addEventListener('change', function () {
        update(root);
      });
    });
  }

  /* ------------------------------------------------------------- sticky bar */

  function bindSticky(root) {
    var sticky = root.querySelector('[data-pdp-sticky]');
    var anchor = root.querySelector('[data-pdp-buy]');
    if (!sticky || !anchor) return;

    var showAfter = parseInt(sticky.getAttribute('data-show-after'), 10);
    if (isNaN(showAfter)) showAfter = 520;

    function onScroll() {
      var past = window.scrollY > anchor.offsetTop + showAfter;
      var nearFooter = window.scrollY + window.innerHeight > document.body.scrollHeight - 140;
      sticky.classList.toggle('is-visible', past && !nearFooter);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    onScroll();
  }

  /* ------------------------------------------------------------------- init */

  function init(root) {
    if (root.hasAttribute('data-pdp-ready')) return;
    root.setAttribute('data-pdp-ready', '');

    bindGallery(root);
    bindOptions(root);
    bindQuantity(root);
    bindSticky(root);
    update(root);
  }

  function initAll() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* Theme editor re-renders the section rather than reloading the page. */
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector(ROOT_SELECTOR);
    if (root) init(root);
  });
})();
